import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import API from "../../../utils/axios";

import { useSnackbar } from "notistack";
import { makeStyles, Tooltip, Typography } from "@material-ui/core";
import CommonMaterialTable from "../commonTable/CommonMaterialTable";
import { toggleTemplate } from "../../../store/actions/account";
import { compare } from "../../../utils/helpers";
import {
  COMMON_TAGS,
  DELIVERED_TAGS,
  EMAIL_TAGS,
  NOT_DELIVERED_TAGS,
  SMS_TAGS,
  TEMPLATE_TYPES
} from "../../../components/data/constants";
import mixpanelWrapper from "../../../utils/mixpanel";
import TitleWithEducationalMaterials from "../../common/TitleWithEducationalMaterials";

const useStyles = makeStyles(theme => ({
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    lineHeight: "0.9375rem",
    fontWeight: 400,
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.5),
    maxWidth: "360px"
  }
}));

function TemplatesOverviewDialog({
  open,
  templates,
  setTemplates,
  setCurrentTemplate,
  handleClose,
  templatesLoaded,
  toggleTemplate,
  setCurrentAction
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    id: null
  });

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  const handleEdit = template => {
    setCurrentTemplate({
      ...template,
      action_type: Object.keys(TEMPLATE_TYPES.actions).find(
        key => TEMPLATE_TYPES.actions[key] === template.action_type
      )
    });
    setCurrentAction("edit");
  };

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const handleAdd = e => {
    setCurrentTemplate(null);
    setCurrentAction("edit");
  };

  const deleteTemplate = id => {
    setLoading(true);
    API.delete(`/v1/templates/${id}`)
      .then(res => {
        setTemplates(templates.filter(el => el.id !== id));
        setSelected(selected.filter(el => el !== id));
        enqueueSnackbar("Notification template deleted.");
      })
      .catch(err => {
        enqueueSnackbar("Something went wrong, couldn't delete template");
      })
      .finally(() => setLoading(false));
  };

  const deleteTemplates = selected => {
    setLoading(true);

    let template;
    if (selected.length === 1) {
      template = templates.find(item => item.id === selected[0]);
    }

    API.delete("/v1/templates", { data: selected })
      .then(res => {
        setTemplates(templates.filter(el => !selected.includes(el.id)));
        setSelected([]);
        enqueueSnackbar(
          !template
            ? `Selected notification templates deleted.`
            : `Notification template deleted.`
        );
      })
      .catch(err => {
        enqueueSnackbar(
          "Something went wrong, couldn't delete selected templates"
        );
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = template => {
    setConfirmDialogState({
      open: true,
      id: template.id,
      title: `Delete template?`,
      body: `Are you sure you want to delete this template? Once deleted, it can not be undone.`,
      action: () => {
        deleteTemplate(template.id);
      }
    });
  };

  const handleMultipleDelete = () => {
    setConfirmDialogState({
      open: true,
      title: `Delete templates?`,
      body: `Are you sure you want to permanently delete ${
        selected.length
      } template${selected.length > 1 ? "s" : ""}?`,
      action: () => {
        deleteTemplates(selected);
      }
    });
  };

  const handleTemplateSwitch = async (template, toggledBefore) => {
    try {
      !toggledBefore && toggleTemplate(template);
      await API.patch(
        `/v1/templates/${template.id}?enable=${!template.is_enable}`
      );
      const changedTemplate = templates.find(
        theTemplate => theTemplate.id === template.id
      );
      templates.forEach(theTemplate => {
        if (
          theTemplate.is_enable &&
          !changedTemplate.is_enable &&
          changedTemplate.id !== theTemplate.id &&
          changedTemplate.action_type === theTemplate.action_type &&
          changedTemplate.template_type === theTemplate.template_type
        ) {
          toggleTemplate(theTemplate);
        }
      });
    } catch (err) {
      toggleTemplate(template);
      enqueueSnackbar("Something went wrong");
    }
  };

  const enableTemplate = template => {
    const enabledTemplate = templates.find(
      el =>
        el.is_enable &&
        el.action_type === template.action_type &&
        el.template_type === template.template_type
    );
    const contentList = [];
    [
      ...Object.keys(COMMON_TAGS),
      ...Object.keys(DELIVERED_TAGS),
      ...Object.keys(NOT_DELIVERED_TAGS),
      ...Object.keys(SMS_TAGS),
      ...Object.keys(EMAIL_TAGS)
    ].forEach(tag => {
      if (
        template.message.includes(`{{${tag}}}`) ||
        template.message.includes(`#${tag}`)
      ) {
        contentList.push(tag);
      }
    });
    if (/\[.*?\]\([^#]+?\)/gm.test(template.message)) {
      contentList.push("custom_link");
    }
    mixpanelWrapper.track("Notification Template Activate", {
      Trigger: template.action_type,
      Channel: template.template_type,
      Content: contentList.join(", ")
    });
    if (enabledTemplate && template.id !== enabledTemplate.id) {
      setConfirmDialogState({
        open: true,
        title: `Enable template?`,
        body: `You already have enabled a template with the same trigger and channel. If you enable this template, the other template will be disabled.`,
        confirmText: "Yes, enable",
        action: () => {
          toggleTemplate(enabledTemplate);
          toggleTemplate(template);
          handleTemplateSwitch(enabledTemplate, true).then(() => {
            handleTemplateSwitch(template, true);
          });
        }
      });
    } else {
      handleTemplateSwitch(template);
    }
  };

  const sortTemplates = templates => {
    return [...templates].sort((a, b) => {
      if (a.action_type === b.action_type) {
        if (a.template_type === b.template_type) {
          return 0;
        }
        return a.template_type > b.template_type ? 1 : -1;
      }
      return a.action_type > b.action_type ? 1 : -1;
    });
  };

  const modifyTemplates = templates => {
    return templates.map(template => {
      return {
        ...template,
        action_type: TEMPLATE_TYPES.actions[template.action_type]
      };
    });
  };

  return (
    <>
      <CommonMaterialTable
        confirmDialogState={confirmDialogState}
        setConfirmDialogState={setConfirmDialogState}
        handleClose={handleClose}
        selected={selected}
        data={loading ? [] : sortTemplates(modifyTemplates(templates))}
        title={
          <TitleWithEducationalMaterials
            title={"Notifications"}
            configId={"notifications"}
            sectionName={"notifications"}
            showArticle
            showDivider
          />
        }
        singular="template"
        plural="templates"
        handleDelete={handleDelete}
        handleMultipleDelete={handleMultipleDelete}
        setSelected={setSelected}
        loading={!templatesLoaded || loading}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        open={open}
        switchAllowed
        handleSwitch={enableTemplate}
        columns={[
          {
            title: "Triggered when",
            field: "action_type",
            editable: "never",
            cellStyle: {
              width: "120px"
            },
            headerStyle: {
              width: "120px"
            },
            searchable: true,
            render: rowData => {
              return (
                <span id="templateRow-trigger">{rowData.action_type}</span>
              );
            }
          },
          {
            title: "Channel",
            field: "template_type",
            editable: "never",

            cellStyle: {
              width: "60px"
            },
            headerStyle: {
              width: "60px"
            },
            searchable: true,
            render: rowData => {
              return (
                <span id="templateRow-channel">
                  {TEMPLATE_TYPES.templates[rowData.template_type]}
                </span>
              );
            }
          },
          {
            title: "Message",
            field: "message",
            editable: "never",

            cellStyle: {
              width: "320px"
            },
            headerStyle: {
              width: "320px"
            },
            searchable: true,
            customSort: (a, b) => compare(a.message.trim(), b.message.trim()),
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.message}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <Typography id="templateRow-message">
                    {rowData.message}
                  </Typography>
                </Tooltip>
              );
            }
          }
        ]}
      />
    </>
  );
}

function mapStateToProps(state) {
  return {};
}

export default connect(mapStateToProps, { toggleTemplate })(
  TemplatesOverviewDialog
);
